import type { DrillStep } from '@/actionBoards/actionBoard.types';
import type { BoardOutputV1 } from '@seeqdev/api/model/BoardOutputV1';
import type { DrillDimensionLevelOutputV1 } from '@seeqdev/api/model/DrillDimensionLevelOutputV1';

export interface HydratedDrillLevel {
  id: string;
  name: string;
  rank: number;
  columnName: string | null;
}

/** `assignedLevels` is the subset of `levels` that maps to a Calculated Table column on this board. */
export interface HydratedDrillDimension {
  id: string;
  name: string;
  levels: HydratedDrillLevel[];
  assignedLevels: HydratedDrillLevel[];
}

export type TileBreadcrumb = {
  dimension: HydratedDrillDimension;
  /** Null when the user navigated above any drill — no specific "from" level to surface. */
  nativeLevel: HydratedDrillLevel | null;
  stack: DrillStep[];
};

export const sortLevelsByRank = (levels: DrillDimensionLevelOutputV1[] | undefined): DrillDimensionLevelOutputV1[] =>
  [...(levels ?? [])].sort((firstLevel, secondLevel) => (firstLevel.rank ?? 0) - (secondLevel.rank ?? 0));

/** Lower-case drill IDs so they match: the V1 serializer upper-cases UUID fields while the
 *  drillAssignments map keys arrive lowercase. */
export const normalizeBoardDrillIds = (board: BoardOutputV1): BoardOutputV1 => {
  const hasDrillData = board.drillAssignments != null || (board.drillDimensions?.length ?? 0) > 0;
  if (!hasDrillData) return board;
  const normalizedAssignments = board.drillAssignments
    ? Object.fromEntries(Object.entries(board.drillAssignments).map(([key, value]) => [key.toLowerCase(), value]))
    : board.drillAssignments;
  const normalizedDimensions = board.drillDimensions?.map((dimension) => ({
    ...dimension,
    id: dimension.id?.toLowerCase(),
    levels: dimension.levels?.map((level) => ({ ...level, id: level.id?.toLowerCase() })),
  }));
  return { ...board, drillAssignments: normalizedAssignments, drillDimensions: normalizedDimensions };
};

/** Keep a drill step only while its level is still assigned to a board column; truncate each
 *  stack at the first invalid step. */
export const pruneDrillStateToBoard = (
  drillState: Readonly<Record<string, DrillStep[]>>,
  board: BoardOutputV1 | undefined,
): Record<string, DrillStep[]> => {
  const drillAssignments = board?.drillAssignments ?? {};
  const levelIdsByDimension = new Map<string, Set<string>>(
    (board?.drillDimensions ?? []).flatMap((dimension) =>
      dimension.id != null
        ? [
            [
              dimension.id,
              new Set((dimension.levels ?? []).map((level) => level.id).filter((id): id is string => id != null)),
            ] as [string, Set<string>],
          ]
        : [],
    ),
  );
  const pruned: Record<string, DrillStep[]> = {};
  for (const [dimensionId, stack] of Object.entries(drillState)) {
    const knownLevels = levelIdsByDimension.get(dimensionId);
    if (knownLevels) {
      const survivors: DrillStep[] = [];
      for (const step of stack) {
        if (!knownLevels.has(step.levelId) || drillAssignments[step.levelId] == null) break;
        survivors.push(step);
      }
      if (survivors.length > 0) pruned[dimensionId] = survivors;
    }
  }
  return pruned;
};

export const hydrateDrillDimensions = (board: BoardOutputV1 | undefined): HydratedDrillDimension[] => {
  const dimensions = board?.drillDimensions ?? [];
  const drillAssignments = board?.drillAssignments ?? {};
  return dimensions.flatMap((dimension) => {
    if (!dimension.id) return [];
    const levels: HydratedDrillLevel[] = sortLevelsByRank(dimension.levels)
      .filter((level): level is DrillDimensionLevelOutputV1 & { id: string } => level.id != null)
      .map((level) => ({
        id: level.id,
        name: level.name ?? '',
        rank: level.rank ?? 0,
        columnName: drillAssignments[level.id] ?? null,
      }));
    return [
      {
        id: dimension.id,
        name: dimension.name ?? '',
        levels,
        assignedLevels: levels.filter((level) => level.columnName != null),
      },
    ];
  });
};

export const nextLevel = (
  dimension: HydratedDrillDimension,
  fromLevelId: string | undefined,
): (HydratedDrillLevel & { columnName: string }) | undefined => {
  const fromIndex = fromLevelId ? dimension.levels.findIndex((level) => level.id === fromLevelId) : -1;
  return dimension.levels
    .slice((fromIndex < 0 ? 0 : fromIndex) + 1)
    .find((level): level is HydratedDrillLevel & { columnName: string } => level.columnName != null);
};

export const breadcrumbsForTile = (
  hydratedDimensions: HydratedDrillDimension[],
  drillState: Readonly<Record<string, DrillStep[]>>,
  tileGroupBy: readonly string[],
): TileBreadcrumb[] => {
  const groupByColumns = new Set(tileGroupBy);
  return hydratedDimensions.flatMap((dimension) => {
    const stack = drillState[dimension.id] ?? [];
    if (stack.length === 0) return [];
    const tileParticipates = dimension.levels.some(
      (level) => level.columnName != null && groupByColumns.has(level.columnName),
    );
    if (!tileParticipates) return [];
    const firstFilter = stack[0].filter;
    const nativeLevel = firstFilter
      ? (dimension.levels.find((level) => level.columnName === firstFilter.columnName) ?? null)
      : null;
    return [{ dimension, nativeLevel, stack }];
  });
};
